// Merlin Visibility Pane (admin) — every conversation Merlin has had, across
// every stream he talks through: the admin Agent Console, the Quality and
// Tech Support Co-Pilots, the portal Analyst tab, the sales-site widget, the
// headless reporters and the pipeline sub-agents (order extractor, quote
// advisor) — including the ones held over email / SMS.
//
// Backend: /api/admin/agent-console/monitor/* (admin-only). Filter / sort /
// page state lives in the URL (monitorFilters.ts) so a view is shareable.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { useGuruWidgetViewContext } from '../hooks/useGuruWidgetViewContext';
import { getConversationDetail, type ConversationDetail } from '../services/agentConsole';
import {
  downloadEmailThreadAttachment,
  fetchConversationChannels,
  fetchConversationEmailThread,
  fetchMonitorPage,
  fetchMonitorSummary,
  type ConversationChannelsRead,
  type MonitorPage,
  type MonitorRow,
  type MonitorSortKey,
  type MonitorSummary,
} from '../services/merlinMonitor';
import { MonitorFilterBar } from '../components/merlinMonitor/MonitorFilterBar';
import { MonitorSummaryCards } from '../components/merlinMonitor/MonitorSummaryCards';
import { MonitorTable } from '../components/merlinMonitor/MonitorTable';
import { TranscriptDrawer } from '../components/merlinMonitor/TranscriptDrawer';
import {
  DEFAULT_FILTERS,
  filtersFromSearchParams,
  filtersToSearchParams,
  toPageParams,
  toSummaryParams,
  toggleSort,
  type MonitorFilterState,
} from '../components/merlinMonitor/monitorFilters';
import { errorText } from '../components/merlinMonitor/monitorDisplay';
import type { AttachmentDownloader, ChainDetail } from '../types/portal';
import styles from './MerlinMonitorPage.module.css';

/** Auto-refresh cadence while the tab is visible — running threads move. */
const REFRESH_MS = 30_000;

export function MerlinMonitorPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';

  const [searchParams, setSearchParams] = useSearchParams();
  const state = useMemo(() => filtersFromSearchParams(searchParams), [searchParams]);
  const setState = useCallback(
    (next: MonitorFilterState) => setSearchParams(filtersToSearchParams(next), { replace: true }),
    [setSearchParams],
  );

  const [page, setPage] = useState<MonitorPage | null>(null);
  const [summary, setSummary] = useState<MonitorSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);

  // Drawer.
  const [selected, setSelected] = useState<MonitorRow | null>(null);
  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [channels, setChannels] = useState<ConversationChannelsRead | null>(null);
  const [emailThread, setEmailThread] = useState<ChainDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  // Serialize the query set so effects re-run only when the query changes.
  const pageKey = JSON.stringify(toPageParams(state));
  const summaryKey = JSON.stringify(toSummaryParams(state));
  const reqSeq = useRef(0);

  const load = useCallback(
    async (opts: { silent?: boolean } = {}) => {
      if (!isAdmin) return;
      const seq = ++reqSeq.current;
      if (!opts.silent) setLoading(true);
      try {
        const [p, s] = await Promise.all([
          fetchMonitorPage(toPageParams(state)),
          fetchMonitorSummary(toSummaryParams(state)),
        ]);
        if (seq !== reqSeq.current) return; // a newer query superseded this one
        setPage(p);
        setSummary(s);
        setError(null);
        setRefreshedAt(new Date());
      } catch (e) {
        if (seq !== reqSeq.current) return;
        setError(errorText(e, 'Failed to load conversations'));
      } finally {
        if (seq === reqSeq.current) setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the serialized query
    [isAdmin, pageKey, summaryKey],
  );

  useEffect(() => {
    void load();
  }, [load]);

  // Background refresh while the tab is visible.
  useEffect(() => {
    if (!isAdmin) return;
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') void load({ silent: true });
    }, REFRESH_MS);
    return () => clearInterval(t);
  }, [isAdmin, load]);

  // Open a row: fetch the transcript + channel crossings, and — when the
  // conversation is bound to a Gmail thread — that thread's email chain.
  const openRow = useCallback((row: MonitorRow) => {
    setSelected(row);
    setDetail(null);
    setChannels(null);
    setEmailThread(null);
    setDetailError(null);
    setDetailLoading(true);
    Promise.all([
      getConversationDetail(row.id),
      fetchConversationChannels(row.id),
      row.email_thread_id ? fetchConversationEmailThread(row.id) : Promise.resolve(null),
    ])
      .then(([d, c, t]) => {
        setDetail(d);
        setChannels(c);
        setEmailThread(t);
      })
      .catch((e) => setDetailError(errorText(e, 'Failed to load the transcript')))
      .finally(() => setDetailLoading(false));
  }, []);

  const closeDrawer = useCallback(() => {
    setSelected(null);
    setDetail(null);
    setChannels(null);
    setEmailThread(null);
    setDetailError(null);
  }, []);

  // Attachments on the bound thread are fetched live from the mailbox the
  // message was synced from (the orders-portal path only knows orders@ mail).
  const selectedId = selected?.id ?? null;
  const threadDownload = useCallback<AttachmentDownloader>(
    (att, emailId) =>
      selectedId
        ? downloadEmailThreadAttachment(selectedId, emailId, att.attachment_id ?? att.filename)
        : Promise.reject(new Error('No conversation selected')),
    [selectedId],
  );

  // Deep link: ?open=<conversation id> opens the drawer on load.
  const openParam = searchParams.get('open');
  useEffect(() => {
    if (!openParam || !page || selected) return;
    const row = page.items.find((r) => r.id === openParam);
    if (row) openRow(row);
  }, [openParam, page, selected, openRow]);

  useGuruWidgetViewContext('merlin_monitor', {
    copilot: 'merlin-monitor',
    conversation_id: selected?.id,
    conversation_title: selected ? (selected.title ?? undefined) : undefined,
    agent_name: selected?.profile_name ?? undefined,
    model_id: selected?.model,
  });

  if (!isAdmin) {
    return (
      <div className={styles.page}>
        <div className={styles.denied}>The Merlin monitor is limited to administrators.</div>
      </div>
    );
  }

  return (
    <div className={styles.page} data-testid="merlin-monitor-page">
      <header className={styles.header}>
        <div>
          <h1>Merlin Visibility Pane</h1>
          <p>
            Every conversation Merlin has had, across every stream: the Agent Console, the
            Quality and Tech Support Co-Pilots, the portal Analyst, the sales-site widget,
            the headless reporters and the pipeline agents, including threads held over
            email and SMS. Click a row for the transcript.
          </p>
        </div>
        <div className={styles.headerRight}>
          {refreshedAt && (
            <span className={styles.refreshed} data-testid="refreshed-at">
              {loading ? 'Refreshing…' : `Updated ${refreshedAt.toLocaleTimeString()}`}
            </span>
          )}
          <button type="button" className={styles.refresh} onClick={() => void load()} disabled={loading}>
            ↻ Refresh
          </button>
        </div>
      </header>

      <MonitorSummaryCards totals={summary?.totals ?? null} facets={summary?.facets ?? null} loading={loading} />

      <MonitorFilterBar
        state={state}
        facets={summary?.facets ?? null}
        streams={summary?.streams ?? []}
        onChange={setState}
        onReset={() => setState({ ...DEFAULT_FILTERS, pageSize: state.pageSize })}
      />

      <MonitorTable
        page={page}
        state={state}
        loading={loading}
        error={error}
        selectedId={selected?.id ?? null}
        onSort={(key: MonitorSortKey) => setState(toggleSort(state, key))}
        onPage={(p) => setState({ ...state, page: p })}
        onPageSize={(size) => setState({ ...state, pageSize: size, page: 0 })}
        onOpen={openRow}
      />

      <TranscriptDrawer
        row={selected}
        detail={detail}
        channels={channels}
        emailThread={emailThread}
        downloadAttachment={threadDownload}
        loading={detailLoading}
        error={detailError}
        onClose={closeDrawer}
      />
    </div>
  );
}

export default MerlinMonitorPage;
